/**
 * Таблица прогона мониторинга — docs/release-2026-09-19.md §3–§4.
 *
 * ЧТО ЭТО. Человек открывает «Прогон мониторинга» в шапке и видит всё, что мониторинг
 * прочитал в последнем прогоне: заголовок, одну строку сути, источник, дату и оценку, — а
 * справа статус: по статье уже есть пост, пост пишется или его можно заказать. Заказ одной
 * строки или нескольких сразу запускает ТОЛЬКО писателя по этим статьям; «только
 * мониторинг» — обход источников без писателя.
 *
 * ПОЧЕМУ В СПИСКЕ ТОЛЬКО ОБХОДЫ. Прогон «по ссылке» (ссылка редактора, выбор строки из этой
 * же таблицы) читает ровно одну статью. Покажи его таблица — и заказ трёх строк заменил бы
 * «последний прогон» на прогон из одной строки, а таблица, из которой заказывали, исчезла
 * бы из-под руки. Поэтому `runs` — только регулярные прогоны, ручные прогоны кнопки и сканы;
 * статус строк при этом считается по ВСЕМ заявкам и черновикам, а не по прогону.
 *
 * Схемы здесь — провод между бэкендом (`domain/monitoring-table.ts`) и интерфейсом
 * (`components/MonitoringRunDialog.tsx`); маршруты — `GET /api/v1/monitoring/*`.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { DRAFT_STATES } from '../draft-state.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';
import { GenerationRequestSchema } from './generation.ts';
import { MonitoringRequestStateSchema } from './monitoring-requests.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

/** Сколько обходов отдаёт список прогонов по умолчанию и не более скольких вообще. */
export const MONITORING_RUNS_DEFAULT_LIMIT = 10;
export const MONITORING_RUNS_MAX_LIMIT = 30;

/**
 * Сколько статей можно заказать одним нажатием. Не про кэш — про очередь: заявок в
 * очереди у бренда не больше `MAX_QUEUED_PER_BRAND` (20), и одно нажатие не имеет права
 * занять её целиком — иначе кнопка «Запустить генерацию» до конца партии отвечает отказом.
 */
export const MONITORING_PICKS_MAX = 10;

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/monitoring/runs?brand=&limit=

/**
 * Вид обхода словами интерфейса:
 *   • `scheduled` — по таймеру (`smm-monitor.timer`);
 *   • `manual` — ручной прогон кнопки «Запустить генерацию» (одна тема писателю);
 *   • `scan` — «только мониторинг»: тем не заводит.
 */
export const MONITORING_RUN_KINDS = ['scheduled', 'manual', 'scan'] as const;
export const MonitoringRunKindSchema = z.enum(MONITORING_RUN_KINDS);
export type MonitoringRunKind = z.output<typeof MonitoringRunKindSchema>;

export const MonitoringRunSummarySchema = z.strictObject({
  run_id: Uuid,
  kind: MonitoringRunKindSchema,
  state: z.enum(['running', 'ok', 'partial', 'failed', 'overlapped']),
  started_at: IsoUtcMs,
  finished_at: IsoUtcMs.nullable(),
  /** Строк оценки в прогоне — ровно столько строк у таблицы. */
  items_total: z.int().nonnegative(),
  /** Из них признанных релевантными итоговым вердиктом. */
  items_relevant: z.int().nonnegative(),
  /** Материалов на входе стадии 0 — со счётом отсеянных эвристикой, которых в таблице нет. */
  materials_seen: z.int().nonnegative(),
  topics_created: z.int().nonnegative(),
  /** Кто нажал: у скана и ручного прогона — человек, у регулярного — `null`. */
  requested_by_login: z.string().nullable(),
  error_text: z.string().nullable(),
});
export type MonitoringRunSummary = z.output<typeof MonitoringRunSummarySchema>;

/**
 * Последняя заявка «только мониторинг» бренда в ЛЮБОМ состоянии. Экран показывает по ней
 * «идёт обход» и ловит момент, когда пора перечитать таблицу; отказ скана виден здесь же.
 */
export const MonitoringScanSchema = z.strictObject({
  request_id: Uuid,
  state: MonitoringRequestStateSchema,
  run_id: Uuid.nullable(),
  requested_by_login: z.string().nullable(),
  outcome_detail: z.string().nullable(),
  created_at: IsoUtcMs,
  taken_at: IsoUtcMs.nullable(),
  finished_at: IsoUtcMs.nullable(),
});
export type MonitoringScan = z.output<typeof MonitoringScanSchema>;

export const MonitoringRunsResponseSchema = z.strictObject({
  /** Обходы, свежий первым. Пустой список — законное «мониторинг ещё не ходил». */
  runs: z.array(MonitoringRunSummarySchema),
  scan: MonitoringScanSchema.nullable(),
});
export type MonitoringRunsResponse = z.output<typeof MonitoringRunsResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/monitoring/runs/:id/items?brand=

/**
 * Статус строки (§3.3) — что с этой статьёй СЕЙЧАС, по всем прогонам и заявкам:
 *   • `none` — поста нет и не пишется: кнопка «Написать» (заливка);
 *   • `writing` — есть заявка в очереди или в работе: «Пишется…», нажать нельзя;
 *   • `draft` — есть живой черновик (не удалённый): «Пост готов» (обводка) ведёт в него;
 *   • `failed` — последняя попытка не удалась, живого черновика нет: снова «Написать»,
 *     причина — во всплывающей подсказке, а не мелким текстом в строке;
 *   • `topic` — по статье уже есть ЖИВАЯ тема (`proposed`/`selected`) без черновика: её
 *     завёл сам мониторинг (регулярный прогон или кнопка), и писатель её возьмёт или уже
 *     пишет. Заказать статью ещё раз нельзя — это был бы второй оплаченный пост о том же
 *     (ревью партии, находка B1). Приоритет: `draft` > `writing` > `topic` > `failed` > `none`.
 * Удалённый черновик постом не считается: человек его выбросил, и статья снова свободна.
 */
export const MonitoringItemStatusSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('none') }),
  z.strictObject({
    kind: z.literal('writing'),
    request_id: Uuid,
    state: z.enum(['queued', 'taken']),
  }),
  z.strictObject({
    kind: z.literal('topic'),
    topic_id: Uuid,
    topic_status: z.enum(['proposed', 'selected']),
  }),
  z.strictObject({
    kind: z.literal('draft'),
    draft_id: Uuid,
    post_no: z.int().positive(),
    draft_status: z.enum(DRAFT_STATES),
  }),
  z.strictObject({
    kind: z.literal('failed'),
    request_id: Uuid,
    detail: z.string().nullable(),
    finished_at: IsoUtcMs,
  }),
]);
export type MonitoringItemStatus = z.output<typeof MonitoringItemStatusSchema>;

export const MonitoringItemSchema = z.strictObject({
  material_id: Uuid,
  title: z.string().min(1),
  /** Канонический адрес статьи — им же её читает мониторинг, когда человек заказывает пост. */
  url: z.string().min(1),
  source_code: z.string().nullable(),
  /** Имя источника из реестра; `null` — статья с хоста вне реестра. */
  source_title: z.string().nullable(),
  source_host: z.string().min(1),
  published_at: IsoUtcMs,
  /** `false` — дата не из источника, а момент выборки: экран помечает её как примерную. */
  published_at_known: z.boolean(),
  stage: z.enum(['prefilter', 'deep']),
  relevant: z.boolean(),
  rating: z.number().min(0).max(1),
  confidence: z.number().min(0).max(1).nullable(),
  relevance: z.number().min(0).max(1).nullable(),
  score: z.number().min(0).max(1).nullable(),
  topic_class: z.string().nullable(),
  gist: z.string().nullable(),
  why: z.string().nullable(),
  angle: z.string().nullable(),
  /** Мониторинг сам завёл по статье тему в ЭТОМ прогоне (его выбор для писателя). */
  picked: z.boolean(),
  status: MonitoringItemStatusSchema,
});
export type MonitoringItem = z.output<typeof MonitoringItemSchema>;

export const MonitoringItemsResponseSchema = z.strictObject({
  run: MonitoringRunSummarySchema,
  /** Строки прогона: по убыванию `rating`, при равенстве — свежие первыми. */
  items: z.array(MonitoringItemSchema),
});
export type MonitoringItemsResponse = z.output<typeof MonitoringItemsResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/monitoring/scan — «только мониторинг»

export const CreateMonitoringScanBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
});
export type CreateMonitoringScanBody = z.output<typeof CreateMonitoringScanBodySchema>;

/**
 * Ответ: заявка скана. 201 — заведена новая; 200 — у бренда уже стоит необслуженный скан, и
 * второе нажатие получает ЕГО, а не второй обход с оплатой модели (частичный UNIQUE 0019).
 */
export const CreateMonitoringScanResponseSchema = z.strictObject({
  scan: MonitoringScanSchema,
  created: z.boolean(),
});
export type CreateMonitoringScanResponse = z.output<typeof CreateMonitoringScanResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/monitoring/picks — «написать по этим статьям»

export const CreateMonitoringPicksBodySchema = z
  .strictObject({
    brand: z.string().regex(BRAND_RE),
    material_ids: z.array(Uuid).min(1).max(MONITORING_PICKS_MAX),
    /** Стиль этих постов; `null` — стиль бренда по умолчанию. */
    style_version_id: Uuid.nullable().default(null),
  })
  .check((ctx) => {
    const ids = ctx.value.material_ids;
    if (new Set(ids).size !== ids.length) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['material_ids'],
        message: 'material_ids_not_unique',
      });
    }
  });
export type CreateMonitoringPicksBody = z.output<typeof CreateMonitoringPicksBodySchema>;

/**
 * Исход по каждой статье ОТДЕЛЬНО: партия — не транзакция «всё или ничего». Статья, по
 * которой пост уже пишется или готов, пропускается с причиной, остальные встают в очередь —
 * отказать всей пачке из-за одной строки, которую другая вкладка успела заказать, значило бы
 * заставить человека собирать выбор заново.
 */
export const MONITORING_PICK_SKIP_REASONS = [
  'already_writing',
  'draft_exists',
  'not_found',
  'queue_full',
] as const;

export const MonitoringPickResultSchema = z.strictObject({
  material_id: Uuid,
  request: GenerationRequestSchema.nullable(),
  skipped: z.enum(MONITORING_PICK_SKIP_REASONS).nullable(),
});
export type MonitoringPickResult = z.output<typeof MonitoringPickResultSchema>;

export const CreateMonitoringPicksResponseSchema = z.strictObject({
  results: z.array(MonitoringPickResultSchema),
});
export type CreateMonitoringPicksResponse = z.output<typeof CreateMonitoringPicksResponseSchema>;
